// Link a Real Estate transaction to one of the brokerage's own loan files (ticket 206).
// The client's public status page shows the linked loan's progress, so linking and
// unlinking both ask first. Both need loans.edit on the server; the buttons hide without it.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Link2, Search, Unlink } from 'lucide-react';
import { api } from '../api';
import { losApi } from '../losApi';
import { useAuth } from '../AuthContext';
import { errorMessage } from '../errors';
import { toast } from '../../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { Input } from '../../components/ui/input';
import DialogShell from '../kit/DialogShell';

const SEARCH_DELAY_MS = 250;
const loanName = (l) => {
  const b = l.urla?.borrower || {};
  return [b.first_name, b.last_name].filter(Boolean).join(' ') || 'Unnamed borrower';
};

export default function LoanLinkControl({ tx, onChange }) {
  const auth = useAuth();
  const canEdit = (auth?.user?.permissions || []).includes('loans.edit');
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [loans, setLoans] = useState([]);
  const [searching, setSearching] = useState(false);
  const base = `/re/transactions/${tx.transaction_id}/link-loan`;

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setSearching(true);
    const timer = setTimeout(() => {
      losApi.listLoanPage({ q: q.trim() || undefined, limit: 10 })
        .then((r) => { if (live) setLoans(r.items || []); })
        .catch(() => { if (live) setLoans([]); })
        .finally(() => { if (live) setSearching(false); });
    }, q.trim() ? SEARCH_DELAY_MS : 0);
    return () => { live = false; clearTimeout(timer); };
  }, [open, q]);

  const close = () => { setOpen(false); setQ(''); };
  const link = async (loan) => {
    if (!(await confirmDialog({ title: 'Link this loan?', description: `The client's status page will show the progress of loan ${loan.loan_number} (${loanName(loan)}).`, confirmText: 'Link loan' }))) return;
    try {
      await api.post(`${base}/${loan.loan_file_id}`);
      toast({ title: 'Loan linked' });
      close();
      onChange();
    } catch (e) { toast({ title: 'Link failed', description: errorMessage(e) }); }
  };
  const unlink = async () => {
    if (!(await confirmDialog({ title: 'Unlink the loan?', description: "The client's status page stops showing the loan. The loan file is not changed.", confirmText: 'Unlink', destructive: true }))) return;
    try {
      await api.delete(base);
      toast({ title: 'Loan unlinked' });
      onChange();
    } catch (e) { toast({ title: 'Unlink failed', description: errorMessage(e) }); }
  };

  const id = tx.transaction_id;
  const btn = 'text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1';
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`loan-link-${id}`}>
      {tx.loan_file_id ? (
        <>
          <Link to={`/portal/loans/${tx.loan_file_id}`} className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-indigo-50 text-indigo-700 hover:underline" data-testid={`loan-link-open-${id}`}>Linked to LoanFile</Link>
          {canEdit && <button onClick={unlink} className={btn} data-testid={`unlink-loan-${id}`}><Unlink size={11} /> Unlink</button>}
        </>
      ) : canEdit && (
        <button onClick={() => setOpen(true)} className={btn} data-testid={`link-loan-${id}`}><Link2 size={11} /> Link loan</button>
      )}
      <DialogShell open={open} onOpenChange={(o) => (o ? setOpen(true) : close())} title="Link a loan file" description="Pick the loan this deal is financed through." size="sm">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by borrower, loan # or address" className="h-9 pl-8 text-sm" data-testid={`link-loan-search-${id}`} />
        </div>
        <div className="max-h-64 overflow-y-auto -mx-1" role="listbox" aria-label="Loan files">
          {loans.map((l) => (
            <button key={l.loan_file_id} type="button" role="option" aria-selected="false" onClick={() => link(l)} data-testid={`link-loan-option-${l.loan_file_id}`}
              className="w-full flex flex-col px-2 py-1.5 rounded-lg text-left text-sm hover:bg-slate-50 focus:bg-slate-50 focus:outline-none">
              <span className="truncate" style={{ color: '#070B5F' }}>{loanName(l)} · {l.loan_number}</span>
              <span className="truncate text-xs text-slate-500">{[l.property_address?.street, l.property_address?.city].filter(Boolean).join(', ') || 'No property address'}</span>
            </button>
          ))}
          {!searching && loans.length === 0 && <div className="px-2 py-3 text-center text-xs text-slate-500" data-testid={`link-loan-empty-${id}`}>No loan files match.</div>}
        </div>
      </DialogShell>
    </span>
  );
}
